import { useEffect, useState } from "react";
import api from "@/lib/api";
import { KeyRound, X, Search } from "lucide-react";
import { toast } from "sonner";

export default function AdminCustomers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [resetTarget, setResetTarget] = useState(null);
  const [password, setPassword] = useState("");

  const load = () => {
    api.get("/admin/users").then(({ data }) => setItems(data)).catch(() => {
      api.get("/customers").then(({ data }) => setItems(data)).catch(() => {});
    });
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((u) => {
    const haystack = `${u.name || ""} ${u.email || ""} ${u.phone || ""} ${u.role || ""}`.toLowerCase();
    return !q || haystack.includes(q.toLowerCase());
  });

  const submitReset = async () => {
    if (!resetTarget) return;
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }

    try {
      await api.patch(`/admin/users/${resetTarget.id}/password`, { password });
      toast.success(`Password reset for ${resetTarget.name || resetTarget.email}`);
      setResetTarget(null);
      setPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password reset failed");
    }
  };

  return (
    <div data-testid="admin-customers">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-3xl text-[#FAF9F6]">User Management</h1>
          <p className="text-sm text-[#FAF9F6]/50">Manage customer, staff, admin, and owner account passwords.</p>
        </div>

        <div className="relative w-full lg:w-80">
          <Search className="w-4 h-4 text-[#D4AF37] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search users..."
            className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 text-[#FAF9F6] pl-9 pr-3 py-2 rounded"
          />
        </div>
      </div>

      <div className="admin-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#D4AF37]/80 text-xs uppercase border-b border-[#D4AF37]/15">
              <th className="p-3">Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Phone</th>
              <th>Points</th>
              <th>Joined</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-b border-white/5">
                <td className="p-3 text-[#FAF9F6]">{u.name || "—"}</td>
                <td className="text-[#FAF9F6]/70">{u.email}</td>
                <td>
                  <span className="text-xs px-2 py-1 rounded bg-[#D4AF37]/10 text-[#D4AF37] uppercase">
                    {u.role}
                  </span>
                </td>
                <td className="text-[#FAF9F6]/70">{u.phone || "—"}</td>
                <td className="text-[#D4AF37]">{u.points || 0}</td>
                <td className="text-xs text-[#FAF9F6]/50">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="text-right pr-3">
                  <button
                    onClick={() => {
                      setResetTarget(u);
                      setPassword("");
                    }}
                    className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:text-[#FAF9F6]"
                    data-testid={`user-reset-${u.id}`}
                  >
                    <KeyRound className="w-3.5 h-3.5" />
                    Reset Password
                  </button>
                </td>
              </tr>
            ))}

            {filtered.length === 0 && (
              <tr>
                <td colSpan="7" className="p-6 text-center text-[#FAF9F6]/40">No users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {resetTarget && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="admin-card p-6 w-full max-w-md">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-heading text-2xl text-[#D4AF37]">Reset Password</h3>
                <p className="text-sm text-[#FAF9F6]/60 mt-1">
                  {resetTarget.name || "Unnamed User"}<br />
                  <span className="text-xs">{resetTarget.email}</span><br />
                  <span className="text-[10px] uppercase text-[#D4AF37]">{resetTarget.role}</span>
                </p>
              </div>
              <button onClick={() => setResetTarget(null)} className="text-[#FAF9F6]/50 hover:text-[#FAF9F6]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <label className="block text-xs text-[#FAF9F6]/50 mb-1">New Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded"
              data-testid="user-new-password"
            />

            <div className="flex gap-2 mt-5">
              <button onClick={submitReset} className="btn-chow flex-1" data-testid="user-reset-save">
                Save Password
              </button>
              <button onClick={() => setResetTarget(null)} className="btn-chow-outline flex-1">
                Cancel
              </button>
            </div>

            <p className="text-[11px] text-[#FAF9F6]/40 mt-4">
              Never publish credentials on the sign-in screen. Share reset passwords privately.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
